import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Avatar,
  Chip,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { logActivitySafe } from '../../utils/logActivitySafe';

/**
 * Dashboard Component
 * Role-based dashboard showing different content based on user role
 */
const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Different dashboard content based on role
  // SuperAdmin has COMPLETELY DIFFERENT dashboard (platform-level only)
  const renderDashboardContent = () => {
    if (!user) return null;

    switch (user.role.code) {
      case 'SUPER_ADMIN':
        // SuperAdmin: Platform-level only - NO school operations
        return <SuperAdminDashboard />;
      case 'ADMIN':
        // Admin: School operator - System Settings + all school modules
        return <AdminDashboard />;
      case 'FACULTY':
        // Faculty: Limited to assigned modules
        return <FacultyDashboard />;
      case 'STUDENT':
        // Student: View-only, own data only
        return <StudentDashboard />;
      default:
        return <DefaultDashboard />;
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.firstName} {user?.lastName}
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          {user?.role.name} Dashboard
        </Typography>

        <Box sx={{ mt: 4 }}>
          {renderDashboardContent()}
        </Box>
      </Container>
    </MainLayout>
  );
};

/**
 * SuperAdmin Dashboard
 * Platform-level dashboard ONLY
 * NO access to school-level modules (Attendance, Students, Fee, etc.)
 */
const SuperAdminDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalSchools: 0,
    activeSchools: 0,
    inactiveSchools: 0,
  });
  const [loading, setLoading] = useState(true);

  // Define fetchStats BEFORE useEffect (no hoisting issues)
  const fetchStats = React.useCallback(async () => {
    try {
      // ALWAYS fetch fresh data from backend - no caching, no derived state
      const response = await axios.get('/schools/dashboard/stats', {
        // Prevent caching
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      // Replace entire state with server response (authoritative)
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch immediately on mount
    fetchStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Listen for school update events (create, update, toggle)
  useEffect(() => {
    const handleSchoolUpdate = () => {
      // Immediately refetch when school is created/updated/toggled
      fetchStats();
    };
    window.addEventListener('schoolUpdated', handleSchoolUpdate);
    return () => window.removeEventListener('schoolUpdated', handleSchoolUpdate);
  }, [fetchStats]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Typography variant="h5" gutterBottom>
            Platform Administrator Dashboard
          </Typography>
          <Typography variant="body1">
            Manage schools, module assignments, and platform settings
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Schools
            </Typography>
            <Typography variant="h4">{stats.totalSchools}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Active Schools
            </Typography>
            <Typography variant="h4">{stats.activeSchools}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Suspended / Inactive Schools
            </Typography>
            <Typography variant="h4">{stats.inactiveSchools}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Platform Management
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            As Platform Administrator, you manage:
          </Typography>
          <ul>
            <li>School onboarding and management</li>
            <li>Module assignments (enable/disable modules for schools)</li>
            <li>Platform policies and settings</li>
            <li>Subscription and license management</li>
            <li>Platform audit logs</li>
          </ul>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            <strong>Note:</strong> You do not have access to school-level operations 
            (Attendance, Students, Fee, etc.). These are managed by School Administrators.
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

/**
 * Admin Dashboard
 * Shows school-specific statistics matching the UI reference
 */
const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    presentToday: 0,
    feePending: 0,
    staffCount: 0,
    attendancePercentage: 0,
    attendanceBreakdown: {
      present: 0,
      absent: 0,
      leave: 0,
    },
    monthCollection: {
      collected: 0,
      pending: 0,
      progress: 0,
    },
    recentEnquiries: [] as any[],
    upcomingExaminations: [] as any[],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [studentsRes, attendanceRes, feeRes, enquiriesRes, examsRes] = await Promise.all([
        axios
          .get('/scholar-register/students', { params: { page: 1, limit: 1 } })
          .catch(() => ({ data: { data: [], meta: { total: 0 } } })),
        axios.get('/attendance').catch(() => ({ data: [] })),
        axios.get('/fee/statistics').catch(() => ({ data: { paidAmount: 0, pendingAmount: 0, totalAmount: 0 } })),
        axios.get('/enquiry?status=PENDING').catch(() => ({ data: [] })),
        axios.get('/examination').catch(() => ({ data: [] })),
      ]);

      const studentsPayload: any = studentsRes?.data;
      const totalStudents = Number(
        studentsPayload?.meta?.total ??
          (Array.isArray(studentsPayload?.data)
            ? studentsPayload.data.length
            : Array.isArray(studentsPayload)
              ? studentsPayload.length
              : 0),
      );

      // Get staff count from users with FACULTY role
      let staffCount = 0;
      try {
        const usersRes = await axios.get('/system-settings/users');
        staffCount = usersRes.data.filter((u: any) => u.role?.code === 'FACULTY').length;
      } catch (error) {
        console.error('Failed to fetch staff count:', error);
      }

      const today = new Date().toISOString().split('T')[0];
      const todayAttendance = attendanceRes.data.filter((a: any) => 
        new Date(a.date).toISOString().split('T')[0] === today
      );

      const present = todayAttendance.filter((a: any) => a.status === 'PRESENT').length;
      const absent = todayAttendance.filter((a: any) => a.status === 'ABSENT').length;
      const leave = todayAttendance.filter((a: any) => a.status === 'EXCUSED' || a.status === 'HALF_DAY').length;
      const total = present + absent + leave;
      const attendancePercentage = total > 0 ? (present / total) * 100 : 0;

      const upcomingExams = examsRes.data
        .filter((e: any) => new Date(e.examDate) >= new Date())
        .sort((a: any, b: any) => new Date(a.examDate).getTime() - new Date(b.examDate).getTime())
        .slice(0, 3);

      setStats({
        totalStudents,
        presentToday: present,
        feePending: feeRes.data.pendingAmount || 0,
        staffCount,
        attendancePercentage,
        attendanceBreakdown: {
          present,
          absent,
          leave,
        },
        monthCollection: {
          collected: feeRes.data.paidAmount || 0,
          pending: feeRes.data.pendingAmount || 0,
          progress: feeRes.data.totalAmount > 0 
            ? ((feeRes.data.paidAmount || 0) / feeRes.data.totalAmount) * 100 
            : 0,
        },
        recentEnquiries: enquiriesRes.data.slice(0, 2),
        upcomingExaminations: upcomingExams,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Top Row - Key Metrics */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#e3f2fd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: '#1976d2' }}>👤</Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.totalStudents.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Students
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: '#2e7d32' }}>✓</Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.presentToday.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Present Today
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#fff3e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: '#f57c00' }}>₹</Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  ₹{(stats.feePending / 100000).toFixed(1)}L
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Fee Pending
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#f3e5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: '#7b1fa2' }}>👥</Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.staffCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Staff Count
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Middle Row - Attendance & Collection */}
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, boxShadow: 2, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Today's Attendance
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
            <Button size="small" variant="text">
              View Details
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 3 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={stats.attendancePercentage}
                size={120}
                thickness={4}
                sx={{
                  color: '#4caf50',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {stats.attendancePercentage.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50' }} />
              <Typography variant="body2">
                Present {stats.attendanceBreakdown.present}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f44336' }} />
              <Typography variant="body2">
                Absent {stats.attendanceBreakdown.absent}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ffc107' }} />
              <Typography variant="body2">
                Leave {stats.attendanceBreakdown.leave}
              </Typography>
            </Box>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, boxShadow: 2, p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            This Month Collection
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            ₹{(stats.monthCollection.collected / 100000).toFixed(2)}L
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Collection Progress
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {stats.monthCollection.progress.toFixed(0)}%
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                backgroundColor: '#e0e0e0',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${stats.monthCollection.progress}%`,
                  height: '100%',
                  backgroundColor: '#4caf50',
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, backgroundColor: '#4caf50', borderRadius: 1 }} />
              <Typography variant="body2">
                Collected ₹{(stats.monthCollection.collected / 100000).toFixed(2)}L
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
              <Typography variant="body2">
                Pending ₹{(stats.monthCollection.pending / 100000).toFixed(2)}L
              </Typography>
            </Box>
          </Box>
        </Card>
      </Grid>

      {/* Bottom Row - Recent Enquiries & Upcoming Examinations */}
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, boxShadow: 2, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Enquiries
            </Typography>
            <Select size="small" value="all" sx={{ minWidth: 120 }}>
              <MenuItem value="all">All Status</MenuItem>
            </Select>
          </Box>
          {stats.recentEnquiries.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              No recent enquiries
            </Typography>
          ) : (
            <Box>
              {stats.recentEnquiries.map((enquiry) => (
                <Box
                  key={enquiry.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <Avatar sx={{ width: 40, height: 40, backgroundColor: '#3b82f6' }}>
                    {enquiry.firstName[0]}
                    {enquiry.lastName[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {enquiry.firstName} {enquiry.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {enquiry.message || 'No message'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(enquiry.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Chip
                    label={enquiry.status}
                    size="small"
                    color={enquiry.status === 'CONTACTED' ? 'success' : 'warning'}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, boxShadow: 2, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upcoming Examinations
            </Typography>
          </Box>
          {stats.upcomingExaminations.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              No upcoming examinations
            </Typography>
          ) : (
            <Box>
              {stats.upcomingExaminations.map((exam) => (
                <Box
                  key={exam.id}
                  sx={{
                    py: 1.5,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {exam.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {new Date(exam.examDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}{' '}
                    •{' '}
                    {exam.startTime
                      ? new Date(exam.startTime).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''}
                  </Typography>
                  {exam.classId && (
                    <Chip label={`Grade ${exam.classId}`} size="small" color="primary" />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

/**
 * Faculty Dashboard
 * Shows class and student-related information
 */
const FacultyDashboard: React.FC = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              My Classes
            </Typography>
            <Typography variant="h4">0</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Today's Attendance
            </Typography>
            <Typography variant="h4">0</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending Homework
            </Typography>
            <Typography variant="h4">0</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            My Schedule
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Faculty dashboard content - View classes, mark attendance, assign homework
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

/**
 * Student Dashboard
 * Shows student-specific information
 */
const StudentDashboard: React.FC = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              My Attendance
            </Typography>
            <Typography variant="h4">-</Typography>
            <Typography variant="body2" color="textSecondary">
              This month
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Assignments Due
            </Typography>
            <Typography variant="h4">0</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Upcoming Exams
            </Typography>
            <Typography variant="h4">0</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Student dashboard content - View attendance, homework, exam results
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

/**
 * Default Dashboard
 * Fallback for unknown roles
 */
const DefaultDashboard: React.FC = () => {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Dashboard
          </Typography>
      <Typography variant="body2" color="textSecondary">
        Welcome to your dashboard
      </Typography>
    </Paper>
  );
};

export default Dashboard;



